import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Alert, Grid, Tabs, Tab,
  CircularProgress, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  SwapHoriz as DelegationIcon,
  NorthEast as OutgoingIcon,
  SouthWest as IncomingIcon,
  Drafts as DraftsIcon,
} from '@mui/icons-material';
import { format, parseISO, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { delegationApi, type DelegationBundle, type DelegationItem } from '../api/services';
import HelpPanel from '../components/HelpPanel';

// ---------------------------------------------------------------------------
// Delegation bundle (Phase 3 task 3.5). Three panels answer "what's pending
// around me?" in one surface: items I delegated down (are they moving?),
// items I'm owning because someone delegated them to me, and drafts waiting
// for me to promote into a plan. The backend round trip is deliberately
// singular so the tabs just switch views without refetching.
// ---------------------------------------------------------------------------

const STATUS_COLOURS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  Backlog: 'default',
  Planned: 'info',
  InProgress: 'warning',
  Done: 'success',
  Deferred: 'default',
  Cancelled: 'error',
};

function DelegationRow({
  item,
  showAssignee,
  showDelegator,
  onClick,
}: {
  item: DelegationItem;
  showAssignee?: boolean;
  showDelegator?: boolean;
  onClick?: () => void;
}) {
  const theme = useTheme();
  let age: string | null = null;
  try {
    const diff = differenceInDays(new Date(), parseISO(item.targetDate));
    if (diff > 0 && item.status !== 'Done') age = `${diff}d overdue`;
    else if (diff <= 0) age = `in ${Math.abs(diff)}d`;
  } catch {
    age = null;
  }

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { bgcolor: theme.palette.action.hover } : undefined,
        borderLeft: 2,
        borderColor: item.priority === 'Critical' ? 'error.main' : item.priority === 'High' ? 'warning.main' : 'divider',
      }}
      onClick={onClick}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 180 }}>
          {item.title}
        </Typography>
        <Chip size="small" label={item.status} color={STATUS_COLOURS[item.status] ?? 'default'} sx={{ height: 22 }} />
        {showAssignee && item.assignedToName && (
          <Chip size="small" label={`To: ${item.assignedToName}`} variant="outlined" sx={{ height: 22 }} />
        )}
        {showDelegator && item.delegatedByName && (
          <Chip size="small" label={`From: ${item.delegatedByName}`} variant="outlined" sx={{ height: 22 }} />
        )}
        {age && (
          <Typography variant="caption" sx={{ color: age.includes('overdue') ? 'error.main' : 'text.secondary', fontWeight: age.includes('overdue') ? 600 : 400 }}>
            {age}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function DelegationPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<DelegationBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await delegationApi.get();
        if (!cancelled) setData(response);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Could not load delegation bundle.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!data) return null;

  const openItem = (id: string) => navigate(`/work-items?id=${id}`);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <DelegationIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">Delegation</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            What you've pushed down, what's been pushed to you, and what's waiting in your drafts tray.
          </Typography>
        </Box>
      </Box>

      <HelpPanel title="About Delegation" storageKey="delegation">
        Delegation rolls the three "pending around me" queues into one screen. Delegated by you means items you pushed to another executive. Delegated to you means items a more senior exec handed to you. Drafts are work items you created but haven't committed to a plan yet, often drafted by AI from a transcript or image capture.
      </HelpPanel>

      {/* Summary strip */}
      <Card sx={{ mb: 2, borderLeft: 3, borderColor: 'primary.main' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 3 }} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" useFlexGap>
            <Typography variant="body2" fontWeight={600}>
              At a glance
            </Typography>
            <Chip size="small" icon={<OutgoingIcon sx={{ fontSize: 16 }} />} label={`${data.delegatedByMe.length} delegated by you`} />
            <Chip size="small" icon={<IncomingIcon sx={{ fontSize: 16 }} />} label={`${data.delegatedToMe.length} delegated to you`} />
            <Chip size="small" icon={<DraftsIcon sx={{ fontSize: 16 }} />} label={`${data.drafts.length} drafts`} />
          </Stack>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab icon={<OutgoingIcon />} iconPosition="start" label={`Delegated by me (${data.delegatedByMe.length})`} />
        <Tab icon={<IncomingIcon />} iconPosition="start" label={`Delegated to me (${data.delegatedToMe.length})`} />
        <Tab icon={<DraftsIcon />} iconPosition="start" label={`Drafts (${data.drafts.length})`} />
      </Tabs>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              {tab === 0 && (
                data.delegatedByMe.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing delegated. When you hand an item to another exec from the Work Items page, it appears here so you can see whether it is moving.
                  </Typography>
                ) : (
                  <Stack spacing={1} divider={<Divider flexItem />}>
                    {data.delegatedByMe.map((it) => (
                      <DelegationRow key={it.id} item={it} showAssignee onClick={() => openItem(it.id)} />
                    ))}
                  </Stack>
                )
              )}
              {tab === 1 && (
                data.delegatedToMe.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No inbound delegations. Items handed to you by a more senior exec land here and, if still open, stay visible until they're done.
                  </Typography>
                ) : (
                  <Stack spacing={1} divider={<Divider flexItem />}>
                    {data.delegatedToMe.map((it) => (
                      <DelegationRow key={it.id} item={it} showDelegator onClick={() => openItem(it.id)} />
                    ))}
                  </Stack>
                )
              )}
              {tab === 2 && (
                data.drafts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Drafts tray is empty. Items typed as Draft appear here until you promote them into a plan or delete them. Transcript extractions and image captures are common sources of drafts.
                  </Typography>
                ) : (
                  <Stack spacing={1} divider={<Divider flexItem />}>
                    {data.drafts.map((it) => (
                      <DelegationRow key={it.id} item={it} onClick={() => openItem(it.id)} />
                    ))}
                  </Stack>
                )
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
